import React, { useEffect, useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, DisplayField } from '../components/FormComponents';
import { mockRequisitions, mockProducts } from '../data/mockData';
export function RequisitionForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockRequisitions.find(r => r.id === id) : null;
  const [formData, setFormData] = useState({
    reqNo: initialData?.reqNo || 'REQ-AUTO-GEN',
    department: initialData?.department || '',
    project: '',
    usageType: initialData?.reqFor || 'Office Use',
    employee: initialData?.employee || '',
    requestedBy: initialData?.requestedBy || 'Admin User',
    requiredDate: initialData?.requiredDate || '',
    requisitionType: 'Goods' // Default to Goods
  });
  const createEmptyLineItem = () => ({
    id: Date.now().toString(),
    product: '',
    qty: 1,
    rate: 0,
    estimatedCost: 0,
    specification: ''
  });
  const [lineItems, setLineItems] = useState([{
    ...createEmptyLineItem(),
    id: '1'
  }]);
  // Reset line items when requisitionType changes
  useEffect(() => {
    setLineItems([{
      ...createEmptyLineItem(),
      id: '1'
    }]);
  }, [formData.requisitionType]);
  const handleLineItemChange = (id: string, field: string, value: any) => {
    setLineItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'qty' || field === 'rate') {
          updated.estimatedCost = updated.qty * updated.rate;
        }
        return updated;
      }
      return item;
    }));
  };
  const handleProductSelect = (id: string, selectedValue: string) => {
    if (formData.requisitionType === 'Goods') {
      const product = mockProducts.find(p => p.productCode === selectedValue);
      if (product) {
        setLineItems(items => items.map(item => {
          if (item.id === id) {
            const rate = product.defaultRate || 0;
            return {
              ...item,
              product: product.productCode,
              rate: rate,
              estimatedCost: item.qty * rate
            };
          }
          return item;
        }));
      } else {
        // Clear selection
        setLineItems(items => items.map(item => item.id === id ? {
          ...item,
          product: '',
          rate: 0,
          estimatedCost: 0
        } : item));
      }
    }
  };
  const addLineItem = () => {
    setLineItems([...lineItems, createEmptyLineItem()]);
  };
  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };
  const totalAmount = lineItems.reduce((sum, item) => sum + item.estimatedCost, 0);
  const getColumnHeader = () => {
    switch (formData.requisitionType) {
      case 'Goods':
        return 'Product';
      case 'Works':
        return 'Works Description';
      case 'Service':
        return 'Service Description';
      default:
        return 'Item';
    }
  };
  const renderItemCell = (item: (typeof lineItems)[0]) => {
    if (formData.requisitionType === 'Goods') {
      return <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 bg-white" value={item.product} onChange={e => handleProductSelect(item.id, e.target.value)} data-id="element-2048">
          <option value="" data-id="element-2049">Select Product...</option>
          {mockProducts.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.productCode} data-id="element-2050">
                {p.productCode} - {p.productName}
              </option>)}
        </select>;
    }
    // Works and Service - free text input
    return <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.product} onChange={e => handleLineItemChange(item.id, 'product', e.target.value)} placeholder={formData.requisitionType === 'Works' ? 'Enter works description...' : 'Enter service description...'} data-id="element-2051" />;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    if (!formData.department) {
      alert('Please select a department.');
      return;
    }
    if (!formData.requiredDate) {
      alert('Please select a required date.');
      return;
    }
    if (formData.usageType === 'Employee Use' && !formData.employee) {
      alert('Please select an employee.');
      return;
    }
    const validItems = lineItems.filter(item => item.product !== '');
    if (validItems.length === 0) {
      alert(`Please add at least one ${formData.requisitionType.toLowerCase()} item.`);
      return;
    }
    const invalidQty = validItems.some(item => item.qty <= 0);
    if (invalidQty) {
      alert('All selected items must have a quantity greater than 0.');
      return;
    }
    alert(`Requisition ${mode === 'create' ? 'submitted' : 'updated'} successfully!`);
    navigate('/requisitions');
  };
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-2052">
      <div className="flex items-center justify-between" data-id="element-2053">
        <div data-id="element-2054">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2055">
            {mode === 'create' ? 'Create Requisition' : 'Edit Requisition'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2056">
            Request goods, works, or services for office or employee use.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-2057">
        <FormSection title="Basic Information" data-id="element-2058">
          <DisplayField label="REQ No" value={formData.reqNo} data-id="element-2059" />
          <DisplayField label="Requested By" value={formData.requestedBy} data-id="element-2060" />

          <SelectField label="Department" name="department" value={formData.department} onChange={e => setFormData({
          ...formData,
          department: e.target.value
        })} required options={[{
          value: 'Sales',
          label: 'Sales'
        }, {
          value: 'IT',
          label: 'IT'
        }, {
          value: 'HR',
          label: 'HR'
        }]} data-id="element-2061" />

          <SelectField label="Project" name="project" value={formData.project} onChange={e => setFormData({
          ...formData,
          project: e.target.value
        })} required options={[{
          value: 'PRJ-IT-100',
          label: 'PRJ-IT-100 (IT Infrastructure)'
        }, {
          value: 'PRJ-HR-200',
          label: 'PRJ-HR-200 (HR Operations)'
        }, {
          value: 'PRJ-FIN-300',
          label: 'PRJ-FIN-300 (Finance Systems)'
        }, {
          value: 'PRJ-OPS-400',
          label: 'PRJ-OPS-400 (Operations)'
        }, {
          value: 'PRJ-MKT-500',
          label: 'PRJ-MKT-500 (Marketing)'
        }]} data-id="element-2062" />

          <div className="md:col-span-1" data-id="element-2063">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2064">
              Usage Type <span className="text-red-500" data-id="element-2065">*</span>
            </label>
            <div className="flex space-x-4 mt-2" data-id="element-2066">
              <label className="flex items-center" data-id="element-2067">
                <input type="radio" name="usageType" value="Office Use" checked={formData.usageType === 'Office Use'} onChange={e => setFormData({
                ...formData,
                usageType: e.target.value
              })} className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" data-id="element-2068" />
                <span className="ml-2 text-sm text-gray-900" data-id="element-2069">Office Use</span>
              </label>
              <label className="flex items-center" data-id="element-2070">
                <input type="radio" name="usageType" value="Employee Use" checked={formData.usageType === 'Employee Use'} onChange={e => setFormData({
                ...formData,
                usageType: e.target.value
              })} className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" data-id="element-2071" />
                <span className="ml-2 text-sm text-gray-900" data-id="element-2072">Employee Use</span>
              </label>
            </div>
          </div>

          {formData.usageType === 'Employee Use' && <SelectField label="Employee" name="employee" value={formData.employee} onChange={e => setFormData({
          ...formData,
          employee: e.target.value
        })} required options={[{
          value: 'Jim Halpert',
          label: 'Jim Halpert'
        }, {
          value: 'Pam Beesly',
          label: 'Pam Beesly'
        }]} data-id="element-2073" />}

          <TextField label="Required Date" name="requiredDate" type="date" value={formData.requiredDate} onChange={e => setFormData({
          ...formData,
          requiredDate: e.target.value
        })} required data-id="element-2074" />
        </FormSection>

        <FormSection title="Requisition Details" data-id="element-2075">
          <div className="md:col-span-2" data-id="element-2076">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2077">
              Requisition Type <span className="text-red-500" data-id="element-2078">*</span>
            </label>
            <div className="flex space-x-6 mt-2" data-id="element-2079">
              <label className="flex items-center" data-id="element-2080">
                <input type="radio" name="requisitionType" value="Goods" checked={formData.requisitionType === 'Goods'} onChange={e => setFormData({
                ...formData,
                requisitionType: e.target.value
              })} className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" data-id="element-2081" />
                <span className="ml-2 text-sm text-gray-900 font-medium" data-id="element-2082">
                  Goods
                </span>
              </label>
              <label className="flex items-center" data-id="element-2083">
                <input type="radio" name="requisitionType" value="Works" checked={formData.requisitionType === 'Works'} onChange={e => setFormData({
                ...formData,
                requisitionType: e.target.value
              })} className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" data-id="element-2084" />
                <span className="ml-2 text-sm text-gray-900 font-medium" data-id="element-2085">
                  Works
                </span>
              </label>
              <label className="flex items-center" data-id="element-2086">
                <input type="radio" name="requisitionType" value="Service" checked={formData.requisitionType === 'Service'} onChange={e => setFormData({
                ...formData,
                requisitionType: e.target.value
              })} className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" data-id="element-2087" />
                <span className="ml-2 text-sm text-gray-900 font-medium" data-id="element-2088">
                  Service
                </span>
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500" data-id="element-2089">
              Changing the requisition type will reset your line items.
            </p>
          </div>
        </FormSection>

        <div className="mb-8" data-id="element-2090">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-2091">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2092">Line Items</h3>
            <button type="button" onClick={addLineItem} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200" data-id="element-2093">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-2094" /> Add Item
            </button>
          </div>

          <div className="overflow-x-auto" data-id="element-2095">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-2096">
              <thead className="bg-gray-50" data-id="element-2097">
                <tr data-id="element-2098">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2099">
                    {getColumnHeader()}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-2100">
                    Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-2101">
                    Rate
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-2102">
                    Est. Cost
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2103">
                    Specification
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16" data-id="element-2104">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2105">
                {lineItems.map(item => <tr key={item.id} data-id="element-2106">
                    <td className="px-4 py-2" data-id="element-2107">{renderItemCell(item)}</td>
                    <td className="px-4 py-2" data-id="element-2108">
                      <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.qty} onChange={e => handleLineItemChange(item.id, 'qty', Number(e.target.value))} data-id="element-2109" />
                    </td>
                    <td className="px-4 py-2" data-id="element-2110">
                      <input type="number" min="0" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.rate} onChange={e => handleLineItemChange(item.id, 'rate', Number(e.target.value))} data-id="element-2111" />
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50" data-id="element-2112">
                      ${item.estimatedCost.toLocaleString()}
                    </td>
                    <td className="px-4 py-2" data-id="element-2113">
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.specification} onChange={e => handleLineItemChange(item.id, 'specification', e.target.value)} placeholder="Optional details..." data-id="element-2114" />
                    </td>
                    <td className="px-4 py-2 text-center" data-id="element-2115">
                      <button type="button" onClick={() => removeLineItem(item.id)} className="text-red-500 hover:text-red-700 p-1" data-id="element-2116">
                        <TrashIcon className="h-4 w-4" data-id="element-2117" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-2118">
                <tr data-id="element-2119">
                  <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-2120">
                    Total Amount:
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-indigo-600" data-id="element-2121">
                    ${totalAmount.toLocaleString()}
                  </td>
                  <td colSpan={2} data-id="element-2122"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2123">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2124">
            Cancel
          </button>
          <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-2125">
            Save as Draft
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-2126">
            Submit Requisition
          </button>
        </div>
      </form>
    </div>;
}